import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { apiRequest } from '../../config/api';
import { AlertCircle, Clock, Calendar, Shield, ArrowRight, RefreshCw } from 'lucide-react';

const TrialExpiredPage = () => {
  const [hospital, setHospital] = useState(null);
  const [subscription, setSubscription] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchStatus = async () => {
      const storedHospital = localStorage.getItem('hospital_data');
      if (!storedHospital) {
        navigate('/hospital/login');
        return;
      }

      try {
        const hospitalData = JSON.parse(storedHospital);
        const statusData = await apiRequest(`get_hospital_status.php?hospital_id=${hospitalData.id}`);
        
        if (statusData.success) {
          setHospital(statusData.hospital);
          setSubscription(statusData.subscription);
        }
      } catch (error) {
        console.error('Failed to fetch hospital status:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchStatus();
  }, [navigate]);

  const handleLogout = () => {
    localStorage.removeItem('hospital_session_token');
    localStorage.removeItem('hospital_data');
    localStorage.removeItem('hospital_user');
    navigate('/hospital/login');
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <RefreshCw className="h-8 w-8 text-blue-400 animate-spin" />
      </div>
    );
  }

  const isTrial = subscription?.plan === 'TRIAL' || (!subscription?.plan && hospital?.trial_end_at);
  const endDate = subscription?.end_at || hospital?.trial_end_at;
  const formattedEndDate = endDate ? new Date(endDate).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  }) : null;

  return (
    <>
      <Helmet>
        <title>Subscription Expired - Dental Hub</title>
      </Helmet>
      
      <div className="min-h-screen flex items-center justify-center p-4">
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.5 }}
          className="w-full max-w-2xl"
        >
          <Card className="glass-effect border-gray-700">
            <CardHeader className="text-center p-6">
              <motion.div
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ delay: 0.2, type: "spring" }}
                className="mx-auto mb-4 w-16 h-16 rounded-full bg-orange-500/20 flex items-center justify-center"
              >
                <AlertCircle className="h-10 w-10 text-orange-400" />
              </motion.div>
              <CardTitle className="text-2xl text-white mb-2">
                {isTrial ? 'Trial Period Ended' : 'Subscription Expired'}
              </CardTitle>
              <CardDescription className="text-gray-300">
                {hospital?.name || 'Your hospital'}
              </CardDescription>
            </CardHeader>
            
            <CardContent className="space-y-6">
              <div className="glass-effect rounded-lg p-6 border border-orange-500/30">
                <div className="flex items-start space-x-4">
                  <Clock className="h-6 w-6 text-orange-400 mt-1 flex-shrink-0" />
                  <div className="flex-1">
                    <h3 className="text-lg font-semibold text-white mb-2">
                      {isTrial ? 'Your Free Trial Has Ended' : 'Your Subscription Has Expired'}
                    </h3>
                    <p className="text-gray-300 mb-4">
                      {isTrial 
                        ? 'Your 7-day free trial period has ended. To continue using Dental Hub, please subscribe to a plan.'
                        : 'Your subscription has expired. Please renew your subscription to continue using Dental Hub.'
                      }
                    </p>
                    {endDate && (
                      <div className="flex items-center space-x-2 text-sm text-gray-400">
                        <Calendar className="h-4 w-4" />
                        <span>Ended on: {formattedEndDate}</span>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              <div className="bg-blue-500/10 border border-blue-500/30 rounded-lg p-4">
                <h4 className="text-white font-semibold mb-2 flex items-center">
                  <Shield className="h-5 w-5 mr-2 text-blue-400" />
                  What happens next?
                </h4>
                <ul className="space-y-2 text-sm text-gray-300">
                  <li className="flex items-start">
                    <span className="text-blue-400 mr-2">•</span>
                    Contact your Dental Hub administrator to renew your subscription
                  </li>
                  <li className="flex items-start">
                    <span className="text-blue-400 mr-2">•</span>
                    Choose between Monthly or Yearly subscription plans
                  </li>
                  <li className="flex items-start">
                    <span className="text-blue-400 mr-2">•</span>
                    Once renewed, you'll regain full access to all features
                  </li>
                </ul>
              </div>

              <div className="flex flex-col sm:flex-row gap-4 pt-4">
                <Button
                  onClick={handleLogout}
                  variant="outline"
                  className="flex-1 border-gray-600 text-gray-300 hover:bg-gray-700"
                >
                  Logout
                </Button>
                <Button
                  onClick={() => window.location.href = 'mailto:support@dentalhub.com?subject=Subscription Renewal Request'}
                  className="flex-1 medical-gradient text-white hover:opacity-90"
                >
                  Contact Support
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </div>
            </CardContent>
          </Card>
        </motion.div>
      </div>
    </>
  );
};

export default TrialExpiredPage;

